import type { FolderDetailRow } from '@listening/frontend/query/folders';
import { cn } from '@listening/utils/react-helpers';
import FolderOutlinedIcon from '@listening/assets/icons/folder-outlined.svg?react';

import { DialogTrigger } from '@listening/frontend/components/radix/dialog';
import { EditPencil, NavArrowLeft, Plus, Xmark } from 'iconoir-react';
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { DashboardQueryError } from '@/components/shared-ui/dashboard-query-error';
import { FileCardV2 } from '@/components/web-fe/design-v2/file-card-v2';
import { ViewModeToggle } from '@/components/web-fe/design-v2/home-v2';
import {
  FOLDER_SORT_MODES,
  folderSortMode,
  sortLabel,
} from '@/components/web-fe/design-v2/library-sort';
import {
  getLibrarySortMode,
  useLibrarySortMode,
  useSetLibrarySortMode,
} from '@listening/frontend/stores/library-sort-store';
import { FolderSkeleton } from '@/components/web-fe/design-v2/skeletons';
import { SortDropdown } from '@/components/web-fe/design-v2/sort-dropdown';
import {
  fieldSurface,
  primaryButtonSurface,
  secondaryButtonSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { DialogCloseProvider } from '@listening/frontend/components/custom/dialog-dropdown';
import { AddFilesToFolderDialog } from '@/components/web-fe/dialogs/add-files-to-folder-dialog';
import { DeleteFolderDialog } from '@/components/web-fe/dialogs/delete-folder-dialog';
import { DashboardPage } from '@/components/web-fe/pages/dashboard/dashboard-page';
import {
  folderAudiosAtom,
  folderAudiosSource,
} from '@listening/frontend/query/audio-lists';
import { folderDetailAtom } from '@listening/frontend/query/folders';
import { useAtom } from '@effect/atom-react';
import { Exit } from 'effect';
import { AsyncResult } from 'effect/reactivity';
import { useHandleValue, useWaterfall } from 'raf-effect-atom';
import { awaitWithinSsrBudget } from '@/lib/ssr-budget';
import {
  successOrThrow,
  useLoadedValue,
} from '@listening/frontend/query/route-loader';
import { useElementOnScreen } from '@/lib/utils';
import * as m from '@/paraglide/messages';
import { folderService } from '@/services/folder-service';
import { useLocalUserPrefsStore } from '@/services/user-service';

export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/files/$folderId',
)({
  component: Page,
  pendingComponent: FolderPendingSkeleton,
  pendingMs: 0,
  errorComponent: DashboardQueryError,
  // A typed 404 on the folder detail means it doesn't exist (or isn't ours)
  // → redirect to the files root; any other folder failure surfaces a scoped,
  // typed error via this route's `errorComponent` instead of bubbling to the
  // root catch boundary. The audio list is best-effort: a failed list must
  // never be mistaken for "folder not found".
  loader: async ({ context: { atoms }, params: { folderId } }) => {
    const [detail] = await Promise.all([
      atoms.ensure(folderDetailAtom, folderId),
      awaitWithinSsrBudget(
        atoms.ensure(folderAudiosSource, {
          folderId,
          sortMode: folderSortMode(getLibrarySortMode()),
        }),
      ),
    ]);
    const folderDetail = successOrThrow(detail, {
      NotFoundError: () => redirect({ to: '/app/home' }),
    });

    // The API also accepts a folder's pre-cutover Mongo ObjectId, so `folderId`
    // here may not be the canonical uuid. Bounce to the canonical URL rather than
    // rendering under the legacy one: every folder write refetches the
    // uuid-keyed reads, so a legacy-keyed page would silently go stale.
    if (folderDetail.id !== folderId) {
      throw redirect({
        to: '/app/files/$folderId',
        params: { folderId: folderDetail.id },
        replace: true,
      });
    }
    return { detail };
  },
});

function Page() {
  return (
    <LoadedPage
      folderDetail={useLoadedValue(
        folderDetailAtom,
        Route.useLoaderData().detail,
      )}
    />
  );
}

const actionButton =
  'flex h-9 items-center gap-1.5 px-3.5 text-[13px] font-medium';

function LoadedPage({ folderDetail }: { folderDetail: FolderDetailRow }) {
  // Server-scoped to this folder + ready status; paged, loaded incrementally
  // via the scroll sentinel below.
  const sortMode = folderSortMode(useLibrarySortMode());
  const setSortMode = useSetLibrarySortMode();
  const folderAudios = { folderId: folderDetail.id, sortMode };
  // The sort is device storage, not URL state, so a sort the loader didn't
  // start starts here.
  const listLoad = useHandleValue(
    folderAudiosSource,
    useWaterfall(folderAudiosSource, folderAudios),
  );
  const [listResult, loadMore] = useAtom(folderAudiosAtom(folderAudios));
  const list = AsyncResult.getOrElse(listResult, () => undefined);
  const audioItems = list?.items ?? [];
  const [sentinelRef, sentinelVisible] = useElementOnScreen();
  const canLoadMore = list?.hasNextPage === true && !list.loadingMore;
  useEffect(() => {
    if (canLoadMore && sentinelVisible) loadMore(undefined);
  }, [canLoadMore, loadMore, sentinelVisible]);

  const viewMode = useLocalUserPrefsStore((s) => s.homeScreenLayout);
  const visibleItems = audioItems;

  const folderName = folderService.utils.folderPathToString(folderDetail.path);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddFilesOpen, setIsAddFilesOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState(folderName);
  const router = useRouter();
  const navigate = useNavigate();
  const updateFolderMutation = folderService.mutations.useUpdateMutation();

  return (
    <DashboardPage>
      <div className='flex flex-col gap-[var(--v2-gap-6)]'>
        <div className='flex flex-wrap items-center justify-between gap-[var(--v2-gap-3)]'>
          <div className='flex min-w-0 grow items-center gap-2'>
            <button
              type='button'
              aria-label={m.common_back()}
              onClick={() => {
                router.history.back();
              }}
              className='flex size-8 shrink-0 items-center justify-center rounded-full text-[var(--v2-text-primary)] transition-colors hover:bg-[var(--v2-chip-bg)]'
            >
              <NavArrowLeft className='size-5' />
            </button>
            {isEditMode ? (
              <input
                disabled={updateFolderMutation.isPending}
                value={newFolderName}
                onChange={(e) => {
                  setNewFolderName(e.target.value);
                }}
                aria-label={m.common_edit()}
                className={cn(
                  fieldSurface,
                  'h-9 w-full max-w-sm px-3 text-[15px] font-medium outline-none focus:border-[var(--v2-outline-strong)]',
                )}
              />
            ) : (
              <>
                <h1
                  className='min-w-0 truncate text-[22px] text-[var(--v2-text-primary)]'
                  style={{ fontWeight: 'var(--v2-heading-weight)' }}
                >
                  {folderName}
                </h1>
                <span className='rounded-full bg-[var(--v2-chip-bg)] px-2 py-0.5 text-[12px] font-medium text-[var(--v2-chip-text)] tabular-nums'>
                  {folderDetail.num_items}
                </span>
              </>
            )}
          </div>
          <div className='flex flex-wrap items-center gap-[var(--v2-gap-3)]'>
            {isEditMode ? (
              <>
                <button
                  type='button'
                  className={cn(secondaryButtonSurface, actionButton)}
                  disabled={updateFolderMutation.isPending}
                  onClick={() => {
                    setIsEditMode(false);
                  }}
                >
                  <Xmark className='size-3.5' />
                  {m.common_cancel()}
                </button>
                <button
                  type='button'
                  className={cn(primaryButtonSurface, actionButton)}
                  disabled={updateFolderMutation.isPending}
                  onClick={() => {
                    void updateFolderMutation
                      .renameFolder({
                        id: folderDetail.id,
                        path: newFolderName.split('/'),
                      })
                      .then((exit) => {
                        if (Exit.isSuccess(exit)) setIsEditMode(false);
                      });
                  }}
                >
                  {m.common_save()}
                </button>
              </>
            ) : (
              <>
                <DialogCloseProvider
                  open={isAddFilesOpen}
                  onOpenChange={setIsAddFilesOpen}
                >
                  <DialogTrigger asChild>
                    <button
                      type='button'
                      className={cn(primaryButtonSurface, actionButton)}
                    >
                      <Plus className='size-3.5' />
                      {m.files_add_files()}
                    </button>
                  </DialogTrigger>
                  {isAddFilesOpen && (
                    <AddFilesToFolderDialog
                      folder={{ id: folderDetail.id, name: folderName }}
                    />
                  )}
                </DialogCloseProvider>
                <button
                  type='button'
                  className={cn(secondaryButtonSurface, actionButton)}
                  onClick={() => {
                    setNewFolderName(folderName);
                    setIsEditMode(true);
                  }}
                >
                  <EditPencil className='size-3.5' />
                  {m.common_edit()}
                </button>
                <DialogCloseProvider>
                  <DialogTrigger asChild>
                    <button
                      type='button'
                      className={cn(
                        secondaryButtonSurface,
                        actionButton,
                        'text-scarlet-400',
                      )}
                    >
                      {m.common_delete()}
                    </button>
                  </DialogTrigger>
                  <DeleteFolderDialog
                    folder={{
                      count: folderDetail.num_items,
                      name: folderName,
                      id: folderDetail.id,
                    }}
                    onDeleted={() => {
                      void navigate({ to: '/app/home', replace: true });
                    }}
                  />
                </DialogCloseProvider>
              </>
            )}
            <ViewModeToggle />
            <SortDropdown
              value={sortMode}
              options={FOLDER_SORT_MODES}
              labelOf={sortLabel}
              onChange={setSortMode}
            />
          </div>
        </div>

        {AsyncResult.isInitial(listLoad) ? null : visibleItems.length === 0 ? (
          <div className='flex flex-col items-center gap-2 py-16 text-center'>
            <FolderOutlinedIcon className='size-10 text-[var(--v2-text-muted)]' />
            <h3
              className='text-[15px] text-[var(--v2-text-primary)]'
              style={{ fontWeight: 'var(--v2-heading-weight)' }}
            >
              {m.files_no_files_in_folder({ path: folderName })}
            </h3>
            <p className='max-w-sm text-[13px] text-[var(--v2-text-muted)]'>
              {m.files_empty_folder_description()}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <div className='flex flex-col'>
            {visibleItems.map((audioItem) => (
              <FileCardV2 key={audioItem.id} audioItem={audioItem} asRow />
            ))}
          </div>
        ) : (
          <div
            className='grid gap-[var(--v2-grid-gap)]'
            style={{
              gridTemplateColumns:
                'repeat(auto-fill, minmax(var(--v2-grid-min), 1fr))',
            }}
          >
            {visibleItems.map((audioItem) => (
              <FileCardV2 key={audioItem.id} audioItem={audioItem} />
            ))}
          </div>
        )}
        <div ref={sentinelRef} />
      </div>
    </DashboardPage>
  );
}

function FolderPendingSkeleton() {
  return (
    <DashboardPage>
      <FolderSkeleton />
    </DashboardPage>
  );
}
